import { Box, IconButton } from "@primer/react";
import { CheckIcon, CopyIcon } from "@primer/octicons-react";
import React, { useState } from "react";
import Text from "../text";
import TextWithEllipse from "../text-with-ellipse";
import { IDataGridColumn, IDataGridFocusedCell } from "./type/IDataGridProps";
import clsx from "clsx";
import styles from "./DataGrid.module.css";
import { formatDisplay } from "./type/ColumnHelper";
interface ICellProps {
  column: IDataGridColumn;
  rowIndex: number;
  rowId: any;
  rowData: any;
  colIndex: number;
  stickyOffsets: any;
  focusedCell?: IDataGridFocusedCell;
  mainColor?: string;
}
const Cell = (props: ICellProps) => {
  const { column, rowId, rowData, colIndex, rowIndex, stickyOffsets } = props;
  const [copied, setCopied] = useState(false);
  const isFocused =
    column.isAllowFocus &&
    rowData["id"] === props.focusedCell?.focusedCell?.rowData["id"] &&
    props.focusedCell?.focusedCell?.dataField === column.dataField;
  const cellStyle = {
    width: column.width,
    minWidth: column.minWidth,
    fontWeight: column.isMainColumn ? "600" : 400,
    whiteSpace: "break-spaces" as const,
    textAlign: column.align,
    ...(rowData && { color: rowData.backgroundColor }),
    position: column.fixed ? "sticky" : "relative",
    left:
      column.fixed === "left"
        ? stickyOffsets.leftOffsets[column.uuid ?? ""]
        : undefined,
    right:
      column.fixed === "right"
        ? stickyOffsets.rightOffsets[column.uuid ?? ""]
        : undefined,

    zIndex: column.fixed ? 15 : 1,
    // background: column.fixed ? "red" : undefined,
    outline: isFocused
      ? `2px solid ${props.mainColor ?? "rgb(9, 105, 218)"}`
      : undefined,
    outlineOffset: isFocused ? "-2px" : undefined,
  };
  const handleFocus =
    column.isAllowFocus &&
      (column.dataField ?? column.id) &&
      props.focusedCell?.onFocusedCellChanged
      ? () =>
        props.focusedCell?.onFocusedCellChanged({
          rowData: rowData,
          dataField: column.dataField ?? column.id ?? "",
        })
      : undefined;
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    let value = "";
    if (column.calculateCellValue) {
      value = column.calculateCellValue(rowData);
    } else {
      value = rowData[column.dataField ?? ""];
    }
    navigator.clipboard.writeText(String(value ?? ""));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderContent = () => {
    let content: React.ReactNode;
    if (column.id === "col_idx") {
      content = <Box>{rowIndex + 1}</Box>;
    } else if (column.cellRender) {
      content = column.cellRender(rowData);
    } else if (column.isTextWithEllipseLine) {
      content = (
        <TextWithEllipse
          lineNumber={1}
          allowToggleText={true}
          text={
            column.isHideZeroValue &&
              rowData[column.dataField ?? ""]?.toString() === "0"
              ? ""
              : (rowData[column.dataField ?? ""]?.toString() ?? "")
          }
        />
      );
    } else {
      content = (
        <Text
          text={
            column.isHideZeroValue &&
              rowData[column.dataField ?? ""]?.toString() === "0"
              ? ""
              : formatDisplay(column, (rowData[column.dataField ?? ""]?.toString() ?? ""))
          }
        />
      );
    }

    if (column.allowCopy) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", justifyContent: column.align === "center" ? "center" : "flex-start", "&:hover .copy-button": { display: "inline-flex" } }}>
          <Box sx={{ minWidth: 0 }}>{content}</Box>
          <Box
            className="copy-button"
            sx={{
              display: copied ? "inline-flex" : "none",
              flexShrink: 0,
              ml: 1,
              mt: -3
            }}
          >
            <IconButton
              icon={copied ? CheckIcon : CopyIcon}
              aria-label="Copy"
              size="small"
              variant="invisible"
              onClick={handleCopy}
              sx={{
                color: copied ? "success.fg" : "fg.muted",
                padding: 0,
                height: "16px",
                width: "16px",
                minWidth: "16px",
                "&:hover": {
                  color: "accent.fg",
                }
              }}
            />
          </Box>
        </Box>
      );
    }

    return content;
  };

  return (
    <td
      key={colIndex}
      style={cellStyle}
      onFocus={handleFocus}
      tabIndex={column.isAllowFocus ? 0 : undefined}
      className={clsx(
        column.fixed ? styles.fixed : "",
        column.fixed === "left" ? styles.left : "",
        column.fixed === "right" ? styles.right : "",
        column.isLastColFixed ? styles.lastFixed : "",
        column.isAllowFocus &&
          rowData["id"] === props.focusedCell?.focusedCell?.rowData["id"] &&
          props.focusedCell?.focusedCell?.dataField === column.dataField
          ? styles.focused
          : "",
      )}
    >
      {renderContent()}
    </td>
  );
};

export default Cell;
